// @ts-strict-ignore
import {AfterViewInit, Component, ElementRef, Injector, ViewChild} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {NotificationService, SimUploadService, SimUtilityService} from "@services";
import {ApiError, MediaType} from "@models";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {animate, query, sequence, stagger, style, transition, trigger} from "@angular/animations";
import {HttpEventType} from "@angular/common/http";
import {DomSanitizer} from "@angular/platform-browser";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";

export interface DialogData_UploadMediaDialogInput extends MatDialogDataBaseInput {
	type: MediaType;
}

export interface DialogData_UploadMediaDialogOutput extends MatDialogDataBaseOutput {
	uploadedFiles?: boolean;
}

@Component({
	selector: "sim-upload-media-dialog",
	templateUrl: "./upload-media-dialog.component.html",
	styleUrls: ["./upload-media-dialog.component.scss"],
	animations: [
		zoomInOnEnterAnimation({duration: 175, delay: 0}),
		zoomOutOnLeaveAnimation({duration: 175, delay: 0}),
		trigger("listStagger", [
			transition("* <=> *", [
				query(
					":enter",
					[
						style({opacity: 0, transform: "translateY(-15px)"}),
						stagger(
							"50ms",
							animate(
								"550ms ease-out",
								style({
									opacity: 1,
									transform: "translateY(0px)",
								}),
							),
						),
					],
					{optional: true},
				),
				query(":leave", animate("200ms", style({opacity: 0})), {
					optional: true,
				}),
			]),
		]),
		trigger("anim", [
			transition("* => void", [
				style({
					height: "*",
					opacity: "1",
					transform: "translateX(0)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".1s ease",
						style({
							height: "0",
							opacity: 0,
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
			transition("void => active", [
				style({
					height: "*",
					opacity: "0",
					transform: "translateX(30px)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".1s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: 1,
							transform: "translateX(0)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
		]),
	],
	standalone: false,
})
export class UploadMediaDialogComponent
	extends BasicMaterialDialog<UploadMediaDialogComponent, DialogData_UploadMediaDialogInput, DialogData_UploadMediaDialogOutput>
	implements AfterViewInit
{
	MediaType = MediaType;
	mediaTypeKey: string;

	SimUtilityService = SimUtilityService;
	afterViewInit: boolean;

	@ViewChild("uploadInput", {static: false}) uploadInput: ElementRef;
	currentSelectedFiles: (File & {
		mediaUrl?: string;
		newName?: string;
		progress?: number;
		isPlaying?: boolean;
		progressUploading?: number;
		isUploading?: boolean;
	})[];
	acceptedFileTypes: string = "*";

	@ViewChild("audioPlayer") audioPlayerRef: ElementRef<HTMLAudioElement>;
	public currentFileIndex: number = -1;
	public uploadingMediaFiles: boolean;
	public uploadedFiles: boolean = false;

	constructor(
		private _simNotificationService: NotificationService,
		private _simUploadService: SimUploadService,
		private domSanitizer: DomSanitizer,
		private _translate: TranslateService,
		_injector: Injector,
	) {
		super(_injector);
	}

	ngAfterViewInit() {
		this.setupForMediaType();
	}

	chooseFiles() {
		if (!this.currentSelectedFiles) {
			this.uploadInput.nativeElement.click();
		} else {
			if (this.currentFileIndex != -1) {
				this.audioPlayerRef.nativeElement.pause();
				this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
				this.currentSelectedFiles[this.currentFileIndex].progress = 0;
			}
			this.currentSelectedFiles = undefined;
			this.uploadInput.nativeElement.value = "";
			this.uploadInput.nativeElement.click();
		}
	}

	handleFileInput(event: Event) {
		const files = (event.target as HTMLInputElement).files;

		if (files?.length === 0) {
			this._simNotificationService.notifyInfo(this._translate.instant(Translations.UPLOAD_MEDIA_DIALOG_NOTIFYERROR));
			this.currentSelectedFiles = undefined;
			return;
		}

		this.currentSelectedFiles = Array.from(files);
		this.currentSelectedFiles.forEach((value) => {
			if (this.data?.type === MediaType.Video) {
				value.mediaUrl = <any>this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(value));
			} else {
				value.mediaUrl = URL.createObjectURL(value);
			}
		});
	}

	timeUpdateAudio($event: Event) {
		const progress = (this.audioPlayerRef.nativeElement.currentTime / this.audioPlayerRef.nativeElement.duration) * 100;
		if (this.currentSelectedFiles[this.currentFileIndex]) {
			this.currentSelectedFiles[this.currentFileIndex].progress = progress;
		}
		if (progress === 100) {
			this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
			this.currentSelectedFiles[this.currentFileIndex].progress = 0;
			this.audioPlayerRef.nativeElement.currentTime = 0;
		}
	}

	toggleMediaPlaying(file, index: number) {
		if (this.currentFileIndex != -1 && this.currentFileIndex !== index) {
			this.audioPlayerRef.nativeElement.pause();
			this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
			this.currentSelectedFiles[this.currentFileIndex].progress = 0;
		}

		if (file.isPlaying) {
			this.audioPlayerRef.nativeElement.pause();
			file.isPlaying = false;
			file.progress = 0;
			this.currentFileIndex = -1;
		} else {
			this.audioPlayerRef.nativeElement.src = file.mediaUrl;
			this.currentFileIndex = index;
			this.audioPlayerRef.nativeElement.play().then(
				() => {
					file.isPlaying = true;
				},
				(err) => {
					this._simNotificationService.notifyError(
						this._translate.instant(Translations.UPLOAD_MEDIA_DIALOG_NOTIFYERROR_MEDIA_FILE),
					);
					file.isPlaying = false;
				},
			);
		}
	}

	deleteFromSelection(i: number) {
		if (this.currentFileIndex === i) {
			this.audioPlayerRef.nativeElement.pause();
			this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
			this.currentSelectedFiles[this.currentFileIndex].progress = 0;
			this.currentFileIndex = -1;
		}

		this.currentSelectedFiles.splice(i, 1);
	}

	uploadFiles() {
		this.uploadedFiles = true;

		this.uploadingMediaFiles = true;

		this.currentSelectedFiles.forEach(async (file) => {
			file.progressUploading = 0;
			file.isUploading = true;

			let result = await this._simUploadService.checkUploadMedia({
				fileName: file.name,
				description: file.newName || file.name,
				category: "",
			});
			if (result?.success) {
				await this._simUploadService
					.uploadMedia({
						uploadedFile: file,
						description: file.newName || file.name,
						category: "",
					})
					.subscribe({
						next: (response) => {
							if (response.type === HttpEventType.Response) {
								if (response.body.success) {
									this._simNotificationService.notifySuccess();

									setTimeout(() => {
										if (file.progressUploading === 100) {
											this.currentSelectedFiles.forEach((item, index, array) => {
												if (item === file) {
													array.splice(index, 1);
												}
											});
										}
									}, 200);
								} else {
									this._simNotificationService.notifyError(response.body.reason);
								}

								file.isUploading = false;
							}

							if (response.type === HttpEventType.UploadProgress) {
								file.progressUploading = Math.round((100 * response.loaded) / response.total);
							}
						},
						error: (err: ApiError) => {
							if (!err.wasLogged) {
								this._simNotificationService.notifyError("Could not upload file!");
							}
						},
					});
			} else {
				this._simNotificationService.notifyError(result?.reason ?? "Could not upload file!");
			}
		});
		console.log("FINISHED UPLOADING...");
	}

	mediaTypeSelected(mType: MediaType) {
		this.data.type = mType;
		this.setupForMediaType();
	}

	public setupForMediaType() {
		this.afterViewInit = true;

		switch (this.data.type) {
			case MediaType.Audio:
				this.acceptedFileTypes = ".mp3, .wav";
				this.mediaTypeKey = this.T.MEDIA_DIALOG_AUDIO;
				break;
			case MediaType.Video:
				this.acceptedFileTypes = ".avi, .mp4";
				this.mediaTypeKey = this.T.MEDIA_DIALOG_VIDEO;
				break;
			case MediaType.Image:
				this.acceptedFileTypes = ".png, .jpg, .jpeg";
				this.mediaTypeKey = this.T.MEDIA_DIALOG_IMAGE;
				break;
			case MediaType.PDF:
				this.acceptedFileTypes = ".pdf";
				this.mediaTypeKey = this.T.MEDIA_DIALOG_PDF;
				break;
			case MediaType.PowerPoint:
				this.acceptedFileTypes = ".pptx";
				this.mediaTypeKey = this.T.MEDIA_DIALOG_POWERPOINT;
				break;
			default:
				this.mediaTypeKey = this.T.DIGITAL_SIGNAGE_MEDIA_HEADING;
		}
	}
}
